import { useAppUser } from '../admin/AppUserContext';
import { Link, useLocation } from 'react-router-dom';
import { UserButton } from '@clerk/clerk-react';
import {
  CheckCircle2,
  ArrowLeft,
  Eye,
  Plus,
  Lightbulb,
  ChevronRight,
  TreePine,
  Target,
  Megaphone,
  CheckCircle,
  ChevronDown,
  Building,
  Home,
  ArrowUp,
  CreditCard,
} from 'lucide-react';
import { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import NotificationBell from './NotificationBell';
import { companiesApi, Company } from '../services/api';
import { CLOUDFLARE_STATIC_ASSETS } from '../config/cloudflareAssets';
import { useCompanyWorkspace } from './companyWorkspaceContext';

interface HeaderProps {
  title?: string;
  subtitle?: string;
  actions?: React.ReactNode;
  showMetrics?: boolean;
  metrics?: {
    companies?: number;
    progress?: number;
    completion?: number;
  };
  fullWidth?: boolean;
  backToStrategy?: {
    url: string;
    label?: string;
  };
  foundationNav?: {
    companyId: string;
    activePage: 'vision' | 'mission' | 'values' | 'objectives';
  };
  company?: {
    name: string;
    logo?: string;
  };
}

export default function Header(props: HeaderProps) {
  const workspace = useCompanyWorkspace();
  const updateCompanyAppearance = workspace?.updateCompanyAppearance;
  const companyName = props.company?.name;
  const companyLogo = props.company?.logo;
  useEffect(() => {
    if (companyName && updateCompanyAppearance)
      updateCompanyAppearance({ name: companyName, logo: companyLogo });
  }, [
    companyName,
    companyLogo,
    updateCompanyAppearance,
    workspace?.company?.name,
    workspace?.company?.logo,
  ]);
  return workspace ? <CompanyPageHeader {...props} /> : <LegacyHeader {...props} />;
}

function CompanyPageHeader({
  title,
  subtitle,
  actions,
  showMetrics,
  metrics,
  backToStrategy,
  foundationNav,
}: HeaderProps) {
  const foundationPages = [
    { id: 'vision', label: 'Vision', icon: Eye },
    { id: 'mission', label: 'Mission', icon: CheckCircle2 },
    { id: 'values', label: 'Values', icon: Plus },
    { id: 'objectives', label: 'Objectives', icon: Lightbulb },
  ] as const;
  const nextFoundationPage =
    foundationNav &&
    foundationPages[foundationPages.findIndex((page) => page.id === foundationNav.activePage) + 1];
  return (
    <header className="company-page-header">
      {backToStrategy && (
        <Link to={backToStrategy.url} className="company-page-back">
          <ArrowLeft size={14} /> {backToStrategy.label || 'Back to strategic plan'}
        </Link>
      )}
      <div className="company-page-header-row">
        <div className="company-page-heading">
          {title && <h1>{title}</h1>}
          {subtitle && <p>{subtitle}</p>}
        </div>
        {actions && <div className="company-page-actions">{actions}</div>}
      </div>
      {showMetrics && metrics && (
        <div className="company-page-metrics">
          {metrics.companies !== undefined && (
            <span>
              Companies <strong>{metrics.companies}</strong>
            </span>
          )}
          {metrics.progress !== undefined && (
            <span>
              Planning progress <strong>{metrics.progress}%</strong>
            </span>
          )}
          {metrics.completion !== undefined && (
            <span>
              Task completion <strong>{metrics.completion}%</strong>
            </span>
          )}
        </div>
      )}
      {foundationNav && (
        <div className="company-foundation-nav">
          <nav className="company-foundation-tabs" aria-label="Foundation sections">
            {foundationPages.map((page) => (
              <Link
                key={page.id}
                to={`/company/${foundationNav.companyId}/strategy/foundation/${page.id}`}
                aria-current={foundationNav.activePage === page.id ? 'page' : undefined}
              >
                <page.icon size={16} /> {page.label}
              </Link>
            ))}
          </nav>
          <Link
            className="dashboard-button dashboard-button-primary"
            to={
              nextFoundationPage
                ? `/company/${foundationNav.companyId}/strategy/foundation/${nextFoundationPage.id}`
                : `/company/${foundationNav.companyId}/strategy`
            }
          >
            {nextFoundationPage ? `Next: ${nextFoundationPage.label}` : 'Return to strategic plan'}{' '}
            <ChevronRight size={16} />
          </Link>
        </div>
      )}
    </header>
  );
}

function LegacyHeader({
  title,
  subtitle,
  actions,
  showMetrics = false,
  metrics,
  fullWidth: _fullWidth = false,
  backToStrategy,
  foundationNav,
  company,
}: HeaderProps) {
  const { user, isSupportSession } = useAppUser();
  const location = useLocation();
  const [isLogoMenuOpen, setIsLogoMenuOpen] = useState(false);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [isLoadingCompanies, setIsLoadingCompanies] = useState(false);
  const logoMenuRef = useRef<HTMLDivElement>(null);

  // Extract company ID from current URL
  const getCompanyIdFromPath = () => {
    const pathMatch = location.pathname.match(/\/company\/([^/]+)/);
    return pathMatch ? pathMatch[1] : null;
  };

  const companyId = getCompanyIdFromPath();

  // Build target URL for company switching - keeps user on same page type
  const buildCompanyUrl = (targetCompanyId: string) => {
    const currentPath = location.pathname;

    // If currently on a company page, replace the company ID but keep the rest of the path
    const companyPathMatch = currentPath.match(/^\/company\/[^/]+(.*)$/);
    if (companyPathMatch) {
      const remainingPath = companyPathMatch[1] || '';
      return `/company/${targetCompanyId}${remainingPath}`;
    }

    // If not on a company page, go to company overview
    return `/company/${targetCompanyId}`;
  };

  // Fetch user's companies
  useEffect(() => {
    const fetchCompanies = async () => {
      if (!user) return;

      setIsLoadingCompanies(true);
      try {
        const response = await companiesApi.list();
        if (response.success && response.data) {
          // Sort by most recently joined (or created)
          const sortedCompanies = response.data.sort((a, b) => {
            const dateA = new Date(a.joinedAt || a.createdAt);
            const dateB = new Date(b.joinedAt || b.createdAt);
            return dateB.getTime() - dateA.getTime();
          });
          setCompanies(sortedCompanies);
        }
      } catch (error) {
        console.error('Failed to fetch companies:', error);
      } finally {
        setIsLoadingCompanies(false);
      }
    };

    fetchCompanies();
  }, [user]);

  // Close menu when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (logoMenuRef.current && !logoMenuRef.current.contains(event.target as Node)) {
        setIsLogoMenuOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <header
      className={`bg-white border-b border-gray-200 ${foundationNav ? 'pb-0' : 'py-3 md:py-4'} flex-shrink-0 px-4 md:px-6`}
    >
      <div className={`${foundationNav ? 'pt-3 md:pt-4' : ''}`}>
        <div className="flex items-center justify-between max-w-none">
          <div className="flex items-center space-x-2 md:space-x-6 min-w-0 flex-1">
            <div className="flex items-center space-x-2 md:space-x-3 relative" ref={logoMenuRef}>
              <button
                onClick={() => setIsLogoMenuOpen(!isLogoMenuOpen)}
                className="flex items-center space-x-2 md:space-x-3 hover:bg-gray-50 p-2 rounded-lg transition-colors group"
              >
                <div className="h-8 w-8 flex items-center justify-center flex-shrink-0">
                  {company?.logo ? (
                    <img
                      src={company.logo}
                      alt={company.name}
                      className="h-8 w-8 rounded object-cover"
                    />
                  ) : (
                    <img
                      src={CLOUDFLARE_STATIC_ASSETS.LOGO_ICON}
                      alt="StratPlan"
                      className="h-8 w-8"
                    />
                  )}
                </div>
                <div className="text-left min-w-0">
                  <div className="flex items-center space-x-1">
                    <h1 className="text-base md:text-xl font-semibold text-gray-900 truncate">
                      {company?.name || 'StratPlan'}
                    </h1>
                    <ChevronDown
                      className={`h-4 w-4 text-gray-400 group-hover:text-gray-600 transition-all duration-200 flex-shrink-0 ${isLogoMenuOpen ? 'rotate-180' : ''}`}
                    />
                  </div>
                  {title ? (
                    <p className="text-xs text-gray-500 hidden sm:block truncate">{title}</p>
                  ) : (
                    <p className="text-xs text-gray-500 hidden sm:block truncate">
                      {company ? 'Strategic Planning' : 'Strategic Planning Platform'}
                    </p>
                  )}
                </div>
              </button>

              {/* Logo Dropdown Menu */}
              <AnimatePresence mode="wait">
                {isLogoMenuOpen && (
                  <motion.div
                    key="logo-menu"
                    initial={{ opacity: 0, y: -10, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: -10, scale: 0.95 }}
                    transition={{ duration: 0.2 }}
                    className="absolute top-full left-0 mt-2 w-80 bg-white rounded-lg shadow-xl border border-gray-200 z-[100]"
                  >
                    <div className="p-4">
                      {/* Navigation Section */}
                      <div className="mb-4">
                        <h3 className="text-sm font-medium text-gray-900 mb-2">Navigation</h3>
                        <div className="space-y-1">
                          <Link
                            to="/dashboard"
                            onClick={() => setIsLogoMenuOpen(false)}
                            className="flex items-center space-x-3 px-3 py-2 rounded-lg text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                          >
                            <Home className="h-4 w-4 text-gray-400" />
                            <span>Dashboard</span>
                          </Link>
                          {companyId && (
                            <>
                              <Link
                                to={`/company/${companyId}`}
                                onClick={() => setIsLogoMenuOpen(false)}
                                className="flex items-center space-x-3 px-3 py-2 rounded-lg text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                              >
                                <Building className="h-4 w-4 text-gray-400" />
                                <span>Company Overview</span>
                              </Link>
                            </>
                          )}
                        </div>
                      </div>

                      {/* Strategic Planning Sections */}
                      {companyId && (
                        <div className="mb-4">
                          <h3 className="text-sm font-medium text-gray-900 mb-2">
                            Strategic Planning
                          </h3>
                          <div className="space-y-1">
                            <Link
                              to={`/company/${companyId}/organization`}
                              onClick={() => setIsLogoMenuOpen(false)}
                              className="flex items-center space-x-3 px-3 py-2 rounded-lg text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                            >
                              <TreePine className="h-4 w-4 text-gray-500" />
                              <span>Organizational Structure</span>
                            </Link>
                            <Link
                              to={`/company/${companyId}/strategy`}
                              onClick={() => setIsLogoMenuOpen(false)}
                              className="flex items-center space-x-3 px-3 py-2 rounded-lg text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                            >
                              <Target className="h-4 w-4 text-gray-500" />
                              <span>Strategic Planning</span>
                            </Link>
                            <div className="flex items-center space-x-3 px-3 py-2 rounded-lg text-sm text-gray-400 cursor-not-allowed">
                              <Megaphone className="h-4 w-4 text-gray-300" />
                              <span>Marketing</span>
                              <span className="text-xs bg-gray-100 px-2 py-1 rounded">
                                Coming Soon
                              </span>
                            </div>
                            <Link
                              to={`/company/${companyId}/execution`}
                              onClick={() => setIsLogoMenuOpen(false)}
                              className="flex items-center space-x-3 px-3 py-2 rounded-lg text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                            >
                              <CheckCircle className="h-4 w-4 text-gray-500" />
                              <span>Execution Hub</span>
                            </Link>
                          </div>
                        </div>
                      )}

                      {/* Companies */}
                      {companies.length > 0 && (
                        <div className="border-t border-gray-100 pt-4">
                          <h3 className="text-sm font-medium text-gray-900 mb-2">My Companies</h3>
                          <div className="space-y-1 max-h-48 overflow-y-auto">
                            {companies.map((comp) => (
                              <Link
                                key={comp.id}
                                to={buildCompanyUrl(comp.id)}
                                onClick={() => setIsLogoMenuOpen(false)}
                                className={`flex items-center space-x-3 px-3 py-2 rounded-lg text-sm transition-colors ${
                                  comp.id === companyId
                                    ? 'bg-gray-100 text-gray-800 border border-gray-300'
                                    : 'text-gray-700 hover:bg-gray-100'
                                }`}
                              >
                                <div className="h-6 w-6 flex-shrink-0">
                                  {comp.logo ? (
                                    <img
                                      src={comp.logo}
                                      alt={comp.name}
                                      className="h-6 w-6 rounded object-cover"
                                    />
                                  ) : (
                                    <div className="h-6 w-6 rounded bg-gray-500 flex items-center justify-center">
                                      <span className="text-xs font-semibold text-white">
                                        {comp.name.charAt(0).toUpperCase()}
                                      </span>
                                    </div>
                                  )}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="font-medium truncate">{comp.name}</p>
                                  <p className="text-xs text-gray-500 capitalize">
                                    {comp.size} • {comp.industry}
                                  </p>
                                </div>
                                {comp.id === companyId && (
                                  <div className="h-2 w-2 bg-gray-500 rounded-full flex-shrink-0"></div>
                                )}
                              </Link>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Quick Actions */}
                      <div className="border-t border-gray-100 pt-4">
                        <h3 className="text-sm font-medium text-gray-900 mb-2">Quick Actions</h3>
                        <div className="space-y-1">
                          {companies.length === 0 && !isLoadingCompanies && (
                            <Link
                              to="/dashboard"
                              onClick={() => setIsLogoMenuOpen(false)}
                              className="flex items-center space-x-3 px-3 py-2 rounded-lg text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                            >
                              <Home className="h-4 w-4 text-gray-400" />
                              <span>Go to Dashboard</span>
                            </Link>
                          )}
                          {isLoadingCompanies && (
                            <div className="flex items-center space-x-3 px-3 py-2 text-sm text-gray-500">
                              <div className="h-4 w-4 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin"></div>
                              <span>Loading companies...</span>
                            </div>
                          )}
                          {companyId && (
                            <Link
                              to={`/company/${companyId}/admin`}
                              onClick={() => setIsLogoMenuOpen(false)}
                              className="flex items-center space-x-3 px-3 py-2 rounded-lg text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                            >
                              <ArrowUp className="h-4 w-4 text-gray-400" />
                              <span>Company Settings</span>
                            </Link>
                          )}
                        </div>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Back to Strategy Button */}
            {backToStrategy && (
              <div className="hidden md:block border-l-0 md:border-l border-gray-200 pl-0 md:pl-6">
                <Link
                  to={backToStrategy.url}
                  className="flex items-center space-x-2 text-sm text-gray-600 hover:text-gray-900 transition-colors"
                >
                  <ArrowLeft className="h-4 w-4" />
                  <span className="hidden sm:inline">
                    {backToStrategy.label || 'Back to Strategy'}
                  </span>
                </Link>
              </div>
            )}

            {/* Page Title */}
            {title && title !== 'StratPlan' && (
              <div
                className={`${backToStrategy ? 'hidden md:block border-l-0 md:border-l border-gray-200 pl-0 md:pl-6' : 'hidden md:block border-l-0 md:border-l border-gray-200 pl-0 md:pl-6'} min-w-0`}
              >
                <h2 className="text-base md:text-lg font-semibold text-gray-900 truncate">
                  {title}
                </h2>
                {subtitle && (
                  <p className="text-xs md:text-sm text-gray-600 truncate">{subtitle}</p>
                )}
              </div>
            )}

            {/* Metrics Overview (optional) */}
            {showMetrics && metrics && (
              <div className="hidden md:flex items-center space-x-4 lg:space-x-6 ml-4 lg:ml-8">
                {metrics.companies !== undefined && (
                  <div className="text-sm">
                    <span className="text-gray-600 hidden lg:inline">Companies:</span>
                    <span className="ml-2 font-semibold text-gray-900">{metrics.companies}</span>
                  </div>
                )}
                {metrics.progress !== undefined && (
                  <div className="text-sm">
                    <span className="text-gray-600 hidden lg:inline">Avg. Progress:</span>
                    <span className="ml-2 font-semibold text-gray-700">{metrics.progress}%</span>
                  </div>
                )}
                {metrics.completion !== undefined && (
                  <div className="text-sm hidden lg:block">
                    <span className="text-gray-600">Task Completion:</span>
                    <span className="ml-2 font-semibold text-gray-700">{metrics.completion}%</span>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="flex items-center space-x-2 md:space-x-4 flex-shrink-0">
            {/* Custom Actions - Always first in right section */}
            {actions && <div className="flex items-center space-x-2">{actions}</div>}

            {/* Strategic Planning Navigation Buttons - Standardized position */}
            {!showMetrics && companyId && (
              <div className="hidden lg:flex items-center space-x-1 border-l-0 lg:border-l border-gray-200 pl-0 lg:pl-4">
                <Link
                  to={`/company/${companyId}/organization`}
                  className="bg-gray-600 text-white p-2 rounded-lg hover:bg-gray-700 transition-colors"
                  title="Organizational Structure"
                >
                  <TreePine className="h-5 w-5" />
                </Link>
                <Link
                  to={`/company/${companyId}/strategy`}
                  className="bg-gray-600 text-white p-2 rounded-lg hover:bg-gray-700 transition-colors"
                  title="Strategic Planning"
                >
                  <Target className="h-5 w-5" />
                </Link>
                <button
                  className="bg-gray-400 text-white p-2 rounded-lg opacity-50 cursor-not-allowed"
                  title="Marketing - Coming Soon"
                  disabled
                >
                  <Megaphone className="h-5 w-5" />
                </button>
                <Link
                  to={`/company/${companyId}/execution`}
                  className="bg-gray-600 text-white p-2 rounded-lg hover:bg-gray-700 transition-colors"
                  title="Execution Hub"
                >
                  <CheckCircle className="h-5 w-5" />
                </Link>
              </div>
            )}

            {/* Fallback buttons when not in a company context */}
            {!showMetrics && !companyId && (
              <div className="hidden lg:flex items-center space-x-1 border-l-0 lg:border-l border-gray-200 pl-0 lg:pl-4">
                <Link
                  to="/dashboard"
                  className="bg-gray-600 text-white p-2 rounded-lg hover:bg-gray-700 transition-colors"
                  title="Dashboard"
                >
                  <TreePine className="h-5 w-5" />
                </Link>
                <Link
                  to="/dashboard"
                  className="bg-gray-600 text-white p-2 rounded-lg hover:bg-gray-700 transition-colors"
                  title="Strategic Planning"
                >
                  <Target className="h-5 w-5" />
                </Link>
                <button
                  className="bg-gray-400 text-white p-2 rounded-lg opacity-50 cursor-not-allowed"
                  title="Marketing - Coming Soon"
                  disabled
                >
                  <Megaphone className="h-5 w-5" />
                </button>
                <Link
                  to="/dashboard"
                  className="bg-gray-600 text-white p-2 rounded-lg hover:bg-gray-700 transition-colors"
                  title="Execution Hub"
                >
                  <CheckCircle className="h-5 w-5" />
                </Link>
              </div>
            )}

            {/* Notification Bell - Standardized position */}
            <div className="border-l-0 md:border-l border-gray-200 pl-0 md:pl-4 flex-shrink-0">
              <NotificationBell />
            </div>

            {/* User Management Section - Always last */}
            <div className="flex items-center space-x-2 md:space-x-3 border-l-0 md:border-l border-gray-200 pl-0 md:pl-4 flex-shrink-0">
              <div className="hidden md:flex items-center space-x-2 text-sm">
                <div className="text-right">
                  <p className="font-medium text-gray-900">
                    {user?.firstName} {user?.lastName}
                  </p>
                  <p className="text-xs text-gray-500">{user?.primaryEmailAddress?.emailAddress}</p>
                </div>
              </div>
              {!isSupportSession && (
                <UserButton
                  appearance={{
                    elements: {
                      avatarBox: 'h-10 w-10',
                      userButtonPopoverCard: 'shadow-lg border border-gray-200',
                      userButtonPopoverActions: 'gap-2',
                    },
                  }}
                  showName={false}
                  userProfileMode="navigation"
                  userProfileUrl="/user-profile"
                  afterSignOutUrl="/"
                >
                  <UserButton.MenuItems>
                    <UserButton.Link
                      label="Subscriptions"
                      labelIcon={<CreditCard className="h-4 w-4" />}
                      href="/billing"
                    />
                  </UserButton.MenuItems>
                </UserButton>
              )}
            </div>
          </div>
        </div>

        {/* Foundation Navigation Submenu */}
        {foundationNav && (
          <div className="pt-3 md:pt-4 pb-3 md:pb-4 border-t border-gray-100">
            <div className="flex flex-col md:flex-row items-stretch md:items-center md:justify-between space-y-3 md:space-y-0">
              <nav className="flex items-center space-x-1 overflow-x-auto pb-2 md:pb-0 -mx-4 px-4 md:mx-0 md:px-0 scrollbar-hide">
                <Link
                  to={`/company/${foundationNav.companyId}/strategy/foundation/vision`}
                  className={`flex items-center px-3 md:px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap min-h-[44px] ${
                    foundationNav.activePage === 'vision'
                      ? 'bg-gray-700 text-white'
                      : 'text-gray-600 hover:text-gray-800 hover:bg-gray-100'
                  }`}
                >
                  <Eye className="h-4 w-4 mr-2 flex-shrink-0" />
                  Vision
                </Link>
                <Link
                  to={`/company/${foundationNav.companyId}/strategy/foundation/mission`}
                  className={`flex items-center px-3 md:px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap min-h-[44px] ${
                    foundationNav.activePage === 'mission'
                      ? 'bg-gray-700 text-white'
                      : 'text-gray-600 hover:text-gray-800 hover:bg-gray-100'
                  }`}
                >
                  <CheckCircle2 className="h-4 w-4 mr-2 flex-shrink-0" />
                  Mission
                </Link>
                <Link
                  to={`/company/${foundationNav.companyId}/strategy/foundation/values`}
                  className={`flex items-center px-3 md:px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap min-h-[44px] ${
                    foundationNav.activePage === 'values'
                      ? 'bg-gray-700 text-white'
                      : 'text-gray-600 hover:text-gray-800 hover:bg-gray-100'
                  }`}
                >
                  <Plus className="h-4 w-4 mr-2 flex-shrink-0" />
                  Values
                </Link>
                <Link
                  to={`/company/${foundationNav.companyId}/strategy/foundation/objectives`}
                  className={`flex items-center px-3 md:px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap min-h-[44px] ${
                    foundationNav.activePage === 'objectives'
                      ? 'bg-gray-700 text-white'
                      : 'text-gray-600 hover:text-gray-800 hover:bg-gray-100'
                  }`}
                >
                  <Lightbulb className="h-4 w-4 mr-2 flex-shrink-0" />
                  Objectives
                </Link>
              </nav>

              {/* Current Section Info and Next Button */}
              <div className="flex items-center justify-between md:justify-end space-x-3 md:space-x-6">
                {/* Section Title */}
                <div className="text-left md:text-right min-w-0">
                  <h2 className="text-base md:text-lg font-semibold text-gray-900 truncate">
                    {foundationNav.activePage === 'vision' && 'Vision Statement'}
                    {foundationNav.activePage === 'mission' && 'Mission Statement'}
                    {foundationNav.activePage === 'values' && 'Company Values'}
                    {foundationNav.activePage === 'objectives' && 'Strategic Objectives'}
                  </h2>
                  <p className="text-xs md:text-sm text-gray-600 truncate hidden sm:block">
                    {foundationNav.activePage === 'vision' && "Where We're Going"}
                    {foundationNav.activePage === 'mission' && 'What We Do Today'}
                    {foundationNav.activePage === 'values' && 'How We Operate'}
                    {foundationNav.activePage === 'objectives' && "What We'll Achieve"}
                  </p>
                </div>

                {/* Next Button */}
                {foundationNav.activePage !== 'objectives' && (
                  <Link
                    to={`/company/${foundationNav.companyId}/strategy/foundation/${
                      foundationNav.activePage === 'vision'
                        ? 'mission'
                        : foundationNav.activePage === 'mission'
                          ? 'values'
                          : foundationNav.activePage === 'values'
                            ? 'objectives'
                            : 'objectives'
                    }`}
                    className="flex items-center px-3 md:px-4 py-2 bg-gray-700 text-white rounded-lg text-sm font-medium hover:bg-gray-800 transition-colors whitespace-nowrap flex-shrink-0"
                  >
                    <span className="hidden sm:inline">Next</span>
                    <ChevronRight className="h-4 w-4 sm:ml-1" />
                  </Link>
                )}

                {/* Finish Button for Objectives */}
                {foundationNav.activePage === 'objectives' && (
                  <Link
                    to={`/company/${foundationNav.companyId}/strategy`}
                    className="flex items-center px-3 md:px-4 py-2 bg-gray-700 text-white rounded-lg text-sm font-medium hover:bg-gray-800 transition-colors whitespace-nowrap flex-shrink-0"
                  >
                    <span className="hidden sm:inline">Complete Foundation</span>
                    <CheckCircle2 className="h-4 w-4 sm:ml-1" />
                  </Link>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </header>
  );
}
